'use client'

// frontend/src/components/brand/Reveal.tsx
//
// Things arriving one at a time. Ported from h2r.
//
// `Reveal` staggers a list by index. `Typewriter` types a line out, once, when
// it changes. `ArrivalGlow` draws a soft ring round a box that just changed.
// All three respect prefers-reduced-motion: with motion reduced they simply
// appear.

import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

/** One item in a sequence, delayed by its position. */
export function Reveal({
  index = 0,
  step = 0.22,
  children,
  className,
}: {
  index?: number
  /** Seconds between neighbours. */
  step?: number
  children: React.ReactNode
  className?: string
}) {
  const reduced = useReducedMotion()
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 8, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        delay: reduced ? 0 : index * step,
        duration: 0.34,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/**
 * A line typed out when its text changes. Retypes only when the text itself
 * changes, so a poll returning the same sentence does not retype it.
 */
export function Typewriter({
  text,
  className,
  speed = 18,
  startDelay = 250,
}: {
  text: string
  className?: string
  /** Milliseconds per character. */
  speed?: number
  startDelay?: number
}) {
  const reduced = useReducedMotion()
  const [shown, setShown] = useState(reduced ? text : '')
  const typedRef = useRef<string | null>(null)

  useEffect(() => {
    if (reduced) {
      setShown(text)
      return
    }
    if (typedRef.current === text) return

    setShown('')
    let index = 0
    let timer: ReturnType<typeof setTimeout>

    const begin = setTimeout(() => {
      const tick = () => {
        index += 1
        setShown(text.slice(0, index))
        if (index < text.length) timer = setTimeout(tick, speed)
        // Remembered only once the whole line has been typed, so React's
        // development double run of this effect types it again.
        else typedRef.current = text
      }
      tick()
    }, startDelay)

    return () => {
      clearTimeout(begin)
      clearTimeout(timer)
    }
  }, [text, speed, startDelay, reduced])

  return (
    <span className={className}>
      {shown}
      {!reduced && shown.length < text.length && (
        <motion.span
          aria-hidden
          animate={{ opacity: [1, 0, 1] }}
          transition={{ repeat: Infinity, duration: 0.9 }}
          className='ml-0.5 inline-block h-[0.95em] w-[2px] translate-y-[0.12em] bg-brand-cornflower'
        />
      )}
    </span>
  )
}

/** A soft ring that draws attention to a box that has just changed. */
export function ArrivalGlow({ active, className }: { active: boolean; className?: string }) {
  if (!active) return null
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 rounded-[inherit]',
        'animate-pulse ring-2 ring-brand-cornflower/50',
        className
      )}
    />
  )
}
